import type { SequencerPartition } from '../sequencer/sequencer-partition.js';
import type { UserContext } from './user-context.js';
import {
  PermissionErrorDetail,
  permissionErrorFromBody,
} from './permission-error-schema.js';

export type {
  PermissionErrorDetailType,
  PermissionErrorDetail,
  PermissionErrorPayloadType,
  PermissionErrorPayload,
  RpcPermissionDeniedBody,
} from './permission-error-schema.js';

export function permissionErrorPayload(error: PermissionError): {
  partition: {
    tenant: string;
    realm: string;
    version: string;
    pattern: string;
    name: string;
    run: number;
  };
  context: {
    authId: string;
    name: string;
    args: Record<string, string>;
    auth: { sub: string; aud: string; iss: string };
  };
  detail: PermissionErrorDetail;
} {
  return {
    partition: { ...error.partition },
    context: {
      authId: error.context.authId,
      name: error.context.name,
      args: { ...error.context.args },
      auth: { ...error.context.auth },
    },
    detail: error.detail,
  };
}

export class PermissionError extends Error {
  readonly context: UserContext;
  readonly partition: SequencerPartition;
  readonly detail: PermissionErrorDetail;

  constructor(opts: {
    context: UserContext;
    partition: SequencerPartition;
    detail: PermissionErrorDetail;
  }) {
    super('no permission');
    this.name = 'PermissionError';
    this.context = opts.context;
    this.partition = opts.partition;
    this.detail = opts.detail;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export const PERMISSION_DENIED_HTTP_STATUS = 403;

export function permissionErrorFromRpcJson(
  body: unknown,
): PermissionError | null {
  const parsed = permissionErrorFromBody(body);
  if (!parsed) return null;
  return new PermissionError({
    partition: parsed.partition as SequencerPartition,
    context: parsed.context as UserContext,
    detail: parsed.detail,
  });
}
